// Public certificate page – accessible without authentication via token link in email.
import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Printer, AlertTriangle } from 'lucide-react'

import type { Certificate, SessionAttendee, Student } from '@/features/classes/stores'
import { Button } from '@/ui/button'

import { getCertVerification } from '../api/certificatesService'
import type { PBCertVerification } from '../api/certificatesService'
import { getCertificateHTML } from '../utils/certificatePdf'

interface PublicCertificatePageProps {
  token: string
}

export function PublicCertificatePage({ token }: PublicCertificatePageProps) {
  const [verification, setVerification] = useState<PBCertVerification | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const printTriggered = useRef(false)

  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      setError(false)
      try {
        const data = await getCertVerification(token)
        if (cancelled) {return}
        if (!data) {
          setError(true)
          return
        }
        setVerification(data)
      } catch {
        if (!cancelled) {setError(true)}
      } finally {
        if (!cancelled) {setLoading(false)}
      }
    }

    void load()
    return () => { cancelled = true }
  }, [token])

  const certificateHtml = useMemo(() => {
    if (!verification) {return ''}

    const cert: Certificate = {
      id: '',
      attendeeId: '',
      certificateNumber: verification.certificateNumber,
      issuedAt: verification.issuedAt,
      expiresAt: verification.expiresAt,
      verificationToken: token,
      revoked: false,
    }

    const attendee: SessionAttendee & { student: Student } = {
      id: '',
      sessionId: '',
      studentId: '',
      registeredAt: verification.issuedAt,
      quizAnswers: {},
      quizScore: null,
      status: 'completed',
      source: 'manual',
      firstName: verification.firstName,
      lastName: verification.lastName,
      student: {
        id: '',
        firstName: verification.firstName,
        lastName: verification.lastName,
        email: '',
        phone: '',
        company: '',
      },
    }

    return getCertificateHTML(
      cert,
      attendee,
      verification.courseName,
      verification.courseId,
      verification.instructorName || 'UBSafety Trainer',
      verification.courseColor || undefined,
    )
  }, [verification, token])

  useEffect(() => {
    if (certificateHtml && !printTriggered.current) {
      printTriggered.current = true
      const timer = setTimeout(() => {
        window.print()
      }, 800)
      return () => { clearTimeout(timer) }
    }
  }, [certificateHtml])

  const handlePrint = () => {
    window.print()
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3 text-slate-600">
          <Loader2 className="w-8 h-8 animate-spin" />
          <p className="text-sm">Loading your certificate…</p>
        </div>
      </div>
    )
  }

  if (error || !verification) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-red-100 p-8 max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-lg font-bold text-navy mb-2">Certificate not found</h1>
          <p className="text-sm text-slate-500">
            We could not locate a certificate for this link. It may have been revoked or the link
            may be incorrect.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white">
      <div className="print:hidden bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div>
            <h1 className="font-semibold text-navy">Your certificate is ready</h1>
            <p className="text-xs text-slate-500">
              Use your browser’s print dialog to save it as a PDF.
            </p>
          </div>
          <Button onClick={handlePrint} className="gap-2">
            <Printer className="w-4 h-4" />
            Print / Save PDF
          </Button>
        </div>
      </div>

      <div className="p-4 print:p-0 flex justify-center">
        <div
          className="certificate-container"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: certificateHtml }}
        />
      </div>

      <style>{`
        @media print {
          body { background: white !important; }
          .certificate-container > * { box-shadow: none !important; border-radius: 0 !important; }
        }
      `}</style>
    </div>
  )
}
